import {useState} from 'react';
import {StyleSheet} from 'react-native';
import {Modal, Portal} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {Fund} from '../../hooks/useDomainData';
import {useGuestyCustomFields} from '../../hooks/useGuestyData';
import {useLayoutInfo} from '../../hooks/useLayoutInfo';
import {useAppSettings} from '../AppSettings';
import {
  DefaultFormikStoreValidator,
  FormikStoreForm,
  FormikStoreType,
  FormikStoreTypes,
} from '../FormikStore';
import {useAlerts} from 'react-native-paper-alerts';

export interface FundEditorProps {
  dialogVisible: boolean;
  setDialogVisible: (visible: boolean) => void;
  fund?: Fund;
}

export function FundEditor(props: FundEditorProps): JSX.Element {
  const queryClient = useQueryClient();
  const Alert = useAlerts();

  const layoutInfo = useLayoutInfo();
  const appSettings = useAppSettings();
  const guestyCustomFields = useGuestyCustomFields();

  const [validating, setValidating] = useState(false);

  const collectionName = 'funds';
  const saveFirestoreObject = useMutation(
    (firestoreObject: Fund) => {
      if (props.fund === undefined) {
        // console.log(
        //   'adding: ' + JSON.stringify(firestoreObject, null, 2),
        // );

        // If there was no date set, delete the date property totally

        return (
          firestore()
            .collection(collectionName)
            // the types of .add and .set are incompatible, but we don't need the return value
            .add(firestoreObject) as Promise<unknown> as Promise<void>
        );
      }

      // console.log(
      //   'saving: ' + JSON.stringify(firestoreObject, null, 2),
      // );
      return firestore()
        .collection(collectionName)
        .doc(props.fund?.ref.id)
        .set(firestoreObject, {merge: true});
    },
    {
      onSettled: () => {
        // console.log('saving succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const themedStyles = StyleSheet.create({
    formikStoreContent: {
      margin: 60,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 120,
      maxHeight: layoutInfo.screen.height - 160,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  let fund: {[key: string]: FormikStoreType} = {
    fundId: Date.now(),
    fund: undefined,
    fundCode: undefined,
    internal: false,
    acceptingReservations: false,
    guestyCreditCustomFieldName: undefined,
    guestyCreditRefundCustomFieldName: undefined,
  };

  if (props.fund !== undefined) {
    fund.fundId = props.fund.fundId;
    fund.fund = props.fund.fund;
    fund.fundCode = props.fund.fundCode;
    fund.internal = props.fund.internal;
    fund.acceptingReservations = props.fund.acceptingReservations;
    fund.guestyCreditCustomFieldName = props.fund.guestyCreditCustomFieldName;
    fund.guestyCreditRefundCustomFieldName =
      props.fund.guestyCreditRefundCustomFieldName;
  }

  return (
    <Portal>
      <Modal
        contentContainerStyle={themedStyles.formikStoreContent}
        visible={props.dialogVisible}
        onDismiss={() => props.setDialogVisible(false)}>
        <FormikStoreForm
          objects={[
            {
              title: `${props.fund ? 'Edit' : 'Create'} Fund`,
              firestoreObject: fund,
              fieldMetadata: [
                {
                  key: 'fundId',
                  name: 'Fund ID',
                  type: FormikStoreTypes.number,
                  required: true,
                  editable: false,
                  errorText: 'Fund ID is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'fund',
                  name: 'Fund',
                  type: FormikStoreTypes.text,
                  required: true,
                  errorText: 'Fund is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'fundCode',
                  name: 'Fund Code',
                  type: FormikStoreTypes.text,
                  required: true,
                  errorText: 'Fund Code is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'internal',
                  name: 'Allowed for Internal',
                  type: FormikStoreTypes.boolean,
                  required: false,
                  errorText: 'Must be true or false',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'acceptingReservations',
                  name: 'Fund currently allows partner reservations',
                  type: FormikStoreTypes.boolean,
                  required: false,
                  errorText: 'Must be true or false',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'guestyCreditCustomFieldName',
                  name: 'Guesty Custom Field Name for Credits',
                  type: FormikStoreTypes.select,
                  required: true,
                  errorText:
                    'Create reservation custom field in Guesty first, then select it here.',
                  selectItems: {
                    items: Array.from(guestyCustomFields ?? []).map(
                      field => field.displayName,
                    ),
                  },
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'guestyCreditRefundCustomFieldName',
                  name: 'Guesty Custom Field Name for Credit Refunds',
                  type: FormikStoreTypes.select,
                  required: true,
                  errorText:
                    'Create reservation custom field in Guesty first, then select it here.',
                  selectItems: {
                    items: Array.from(guestyCustomFields ?? []).map(
                      field => field.displayName,
                    ),
                  },
                  validator: DefaultFormikStoreValidator,
                },
              ],
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              validate: false,
              onPress: () => {
                props.setDialogVisible(false);
              },
            },
            {
              text: 'Save',
              validate: true,
              onPress: formObjects => {
                // console.log(
                //   'saving with ' +
                //     JSON.stringify(formObjects[0].firestoreObject, null, 2),
                // );
                setValidating(true);
                saveFirestoreObject.mutate(
                  formObjects[0].firestoreObject as unknown as Fund,
                );
                props.setDialogVisible(false);
              },
            },
          ]}
          validateForm={validating}
        />
      </Modal>
    </Portal>
  );
}
